import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[var(--bg)] px-5 text-[var(--text)]">
      <div className="noise-overlay" />
      <a
        href="/"
        className="absolute left-6 top-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-2)] transition-colors hover:text-[var(--text)]"
      >
        <ArrowLeft size={14} /> Back
      </a>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-[var(--border)] bg-[var(--surface)] p-8 sm:p-10"
      >
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[var(--text)] text-[var(--bg)]">
            <Lock size={18} />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--text-2)]">
              Restricted
            </p>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight">
              Admin Login
            </h1>
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-sm border border-[var(--border)] bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--accent)]"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-sm border border-[var(--border)] bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--accent)]"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="font-mono text-xs text-red-500">
              {error}
            </p>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-2 rounded-sm bg-[var(--text)] py-3.5 font-mono text-xs uppercase tracking-widest text-[var(--bg)] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
